import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarDays, Plus, Save, Trash2, Users, Search, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const PRESENCE_OPTIONS = ["Presente", "Assente"];

const presenceColor = (v) => {
  const map = {
    Presente: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    Assente: "text-rugby-red border-rugby-red/40 bg-rugby-red/10",
  };
  return map[v] || "text-zinc-400 border-zinc-500/40 bg-zinc-500/10";
};

export default function Attivita() {
  const [athletes, setAthletes] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [presenceTypes, setPresenceTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [activityType, setActivityType] = useState("");
  const [notes, setNotes] = useState("");
  const [attMap, setAttMap] = useState({});
  const [search, setSearch] = useState("");
  const [readOnly, setReadOnly] = useState(false);

  const loadAll = () => {
    api.get("/athletes").then((r) => setAthletes(r.data));
    api.get("/activity-types").then((r) => setActivityTypes(r.data));
    api.get("/presence-types").then((r) => setPresenceTypes(r.data));
    api.get("/activities").then((r) => setActivities(r.data));
  };

  useEffect(loadAll, []);

  const resetForm = () => {
    setEditing(null);
    setReadOnly(false);
    setDate(format(new Date(), "yyyy-MM-dd"));
    setActivityType("");
    setNotes("");
    setAttMap({});
    setSearch("");
  };

  const openNew = () => {
    resetForm();
    setOpen(true);
  };

  const populate = (act) => {
    setEditing(act);
    setDate(act.date);
    setActivityType(act.activity_type);
    setNotes(act.notes || "");
    const m = {};
    act.attendances.forEach((a) => (m[a.athlete_id] = a.presence_type));
    setAttMap(m);
    setSearch("");
  };

  const openEdit = (act) => {
    populate(act);
    setReadOnly(false);
    setOpen(true);
  };

  const openView = (act) => {
    populate(act);
    setReadOnly(true);
    setOpen(true);
  };

  const setPresence = (athleteId, val) => {
    setAttMap((m) => ({ ...m, [athleteId]: val }));
  };

  const bulkSet = (val) => {
    const m = {};
    athletes.forEach((a) => (m[a.id] = val));
    setAttMap(m);
  };

  const save = async () => {
    if (!activityType) {
      toast.error("Seleziona prima il tipo di attività");
      return;
    }
    const attendances = athletes
      .filter((a) => attMap[a.id])
      .map((a) => ({
        athlete_id: a.id,
        cognome: a.cognome,
        nome: a.nome,
        presence_type: attMap[a.id],
      }));
    const payload = { date, activity_type: activityType, notes, attendances };
    try {
      if (editing) {
        await api.put(`/activities/${editing.id}`, payload);
        toast.success("Attività aggiornata");
      } else {
        await api.post("/activities", payload);
        toast.success("Attività registrata");
      }
      setOpen(false);
      resetForm();
      loadAll();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const remove = async (act) => {
    await api.delete(`/activities/${act.id}`);
    toast.success("Attività eliminata");
    loadAll();
  };

  const filteredAthletes = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return athletes;
    return athletes.filter(
      (a) => a.cognome.toLowerCase().includes(q) || a.nome.toLowerCase().includes(q)
    );
  }, [athletes, search]);

  const countMarked = Object.values(attMap).filter(Boolean).length;

  return (
    <div data-testid="attivita-page">
      <PageHeader title="Attività" subtitle="Registra le presenze per allenamenti, partite ed eventi">
        <Button
          onClick={openNew}
          data-testid="new-activity-btn"
          className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
        >
          <Plus className="h-4 w-4 mr-2" /> Nuova Attività
        </Button>
      </PageHeader>

      {activities.length === 0 ? (
        <div className="border border-border bg-card p-12 text-center">
          <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nessuna attività registrata. Inizia creando la prima.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {activities.map((act) => {
            const present = act.attendances.filter((a) => a.presence_type === "Presente").length;
            return (
              <div
                key={act.id}
                data-testid={`activity-card-${act.id}`}
                className="border border-border bg-card p-5 hover:border-rugby-yellow/40 transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-head uppercase tracking-wide text-rugby-yellow text-sm font-bold">
                      {act.activity_type}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground mt-1">
                      {format(new Date(act.date), "EEEE d MMMM yyyy", { locale: it })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-head font-bold text-2xl leading-none">{present}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Presenti</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
                  <Users className="h-3.5 w-3.5" /> {act.attendances.length} registrazioni
                </div>
                {act.notes && <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{act.notes}</p>}
                <div className="flex gap-2 mt-auto pt-3 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`view-activity-${act.id}`}
                    onClick={() => openView(act)}
                    className="flex-1 border-border hover:border-rugby-yellow hover:text-rugby-yellow font-head uppercase text-xs tracking-wide"
                  >
                    <Eye className="h-4 w-4 mr-1.5" /> Visualizza
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`edit-activity-${act.id}`}
                    onClick={() => openEdit(act)}
                    className="flex-1 border-border hover:border-rugby-yellow hover:text-rugby-yellow font-head uppercase text-xs tracking-wide"
                  >
                    <Pencil className="h-4 w-4 mr-1.5" /> Modifica
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`delete-activity-${act.id}`}
                    onClick={() => remove(act)}
                    className="border-rugby-red/40 text-rugby-red hover:bg-rugby-red/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b0b0b] border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-head uppercase tracking-tight text-2xl">
              {readOnly ? "Visualizza Attività" : editing ? "Modifica Attività" : "Nuova Attività"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Tipo Attività *
              </Label>
              <Select value={activityType} onValueChange={setActivityType} disabled={readOnly}>
                <SelectTrigger data-testid="activity-type-select" className="bg-black/50 border-border">
                  <SelectValue placeholder="Seleziona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {activityTypes.map((t) => (
                    <SelectItem key={t.id} value={t.value}>{t.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Data
              </Label>
              <Input
                type="date"
                data-testid="activity-date-input"
                value={date}
                disabled={readOnly}
                onChange={(e) => setDate(e.target.value)}
                className="bg-black/50 border-border focus-visible:ring-rugby-yellow"
              />
            </div>
          </div>

          <div className="space-y-2 mt-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Note</Label>
            <Textarea
              value={notes}
              data-testid="activity-notes-input"
              disabled={readOnly}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-black/50 border-border focus-visible:ring-rugby-yellow"
              rows={2}
            />
          </div>

          {activityType ? (
            <div className="mt-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                <div className="font-head uppercase tracking-wide text-sm font-bold">
                  Presenze <span className="text-rugby-yellow">({countMarked})</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {!readOnly && PRESENCE_OPTIONS.map((p) => (
                    <button
                      key={p}
                      data-testid={`bulk-${p}`}
                      onClick={() => bulkSet(p)}
                      className="text-[10px] uppercase tracking-wide px-2 py-1 border border-border hover:border-rugby-yellow text-muted-foreground hover:text-rugby-yellow transition-colors"
                    >
                      Tutti: {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filtra atleti..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-black/50 border-border focus-visible:ring-rugby-yellow"
                />
              </div>

              <div className="border border-border max-h-[320px] overflow-y-auto">
                {filteredAthletes.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`attendance-row-${a.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border last:border-b-0 hover:bg-white/5"
                  >
                    <div className="min-w-0">
                      <span className="font-head font-semibold uppercase text-sm">{a.cognome}</span>{" "}
                      <span className="text-muted-foreground text-sm">{a.nome}</span>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
                      {PRESENCE_OPTIONS.map((p) => {
                        const active = attMap[a.id] === p;
                        return (
                          <button
                            key={p}
                            data-testid={`presence-${a.id}-${p}`}
                            disabled={readOnly}
                            onClick={() => !readOnly && setPresence(a.id, active ? "" : p)}
                            className={`text-[10px] uppercase tracking-wide font-bold px-2.5 py-1 border transition-colors ${
                              active ? presenceColor(p) : "border-border text-muted-foreground hover:text-white"
                            } ${readOnly ? "cursor-default opacity-90" : ""}`}
                          >
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-4 text-sm text-muted-foreground border border-dashed border-border p-6 text-center">
              Seleziona prima il tipo di attività per registrare le presenze.
            </div>
          )}

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-border">
            {readOnly ? (
              <Button
                onClick={() => setOpen(false)}
                data-testid="close-activity-btn"
                className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
              >
                Chiudi
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
                <Button
                  onClick={save}
                  data-testid="save-activity-btn"
                  className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
                >
                  <Save className="h-4 w-4 mr-2" /> {editing ? "Aggiorna" : "Registra"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
